import { Switch, SwitchProps } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import {
  FontSizeOption,
  StatChartSpec,
  StatChartStepOption,
  StatChartThresholdOptions,
  UNIT_SETTINGS_NAME,
} from '@/core';
import {
  FontSizeSelector,
  FontSizeSelectorProps,
  OptionsEditorColumn,
  OptionsEditorControl,
  OptionsEditorGrid,
  OptionsEditorGroup,
  SettingsAutocomplete,
  SettingsAutocompleteOption,
  UnitSelector,
  UnitSelectorProps,
} from '@/plugin-editor-components';
import { ThresholdsEditorProps } from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import { StatChartThresholdsEditor } from './StatChartThresholdsEditor';
import { StatChartOptionsEditorProps } from './stat-chart-model';

interface SeriesNameAutocompleteOption extends SettingsAutocompleteOption {
  value: StatChartSpec['show_series_name'];
  label: string;
}
const SHOW_SERIES_NAME_OPTIONS: SeriesNameAutocompleteOption[] = [
  { id: 'default', label: 'Auto Show', value: undefined },
  { id: 'always', label: 'Always Show', value: 'Always' },
  { id: 'never', label: 'Never Show', value: 'Never' },
];

export function StatChartOptionsEditorSettings(props: StatChartOptionsEditorProps) {
  const { onChange, value } = props;

  const handleUnitChange: UnitSelectorProps['onChange'] = (newUnit) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        draft.unit = newUnit;
      })
    );
  };

  const handleSparklineChange: SwitchProps['onChange'] = (_: unknown, checked: boolean) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        // For now, setting to an empty object when checked, so the stat chart
        // uses the default chart color and line styles. In the future, this
        // will likely be configurable in the UI.
        draft.sparkline = checked ? {} : undefined;
      })
    );
  };

  const handleThresholdsChange: ThresholdsEditorProps<StatChartStepOption, StatChartThresholdOptions>['onChange'] = (
    thresholds
  ) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        const localThresholds = { ...thresholds };
        if (localThresholds.default_color !== undefined && thresholds.base !== undefined) {
          delete localThresholds.default_color;
        }

        draft.thresholds = localThresholds;
      })
    );
  };

  const handleFontSizeChange: FontSizeSelectorProps['onChange'] = (fontSize: FontSizeOption) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        draft.value_font_size = fontSize;
      })
    );
  };

  const handleSeriesNameFontSizeChange: FontSizeSelectorProps['onChange'] = (fontSize: FontSizeOption) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        draft.series_name_font_size = fontSize;
      })
    );
  };

  const handleMessageFontSizeChange: FontSizeSelectorProps['onChange'] = (fontSize: FontSizeOption) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        draft.message_font_size = fontSize;
      })
    );
  };

  const handleShowSeriesNameChange = (_: unknown, option: SeriesNameAutocompleteOption) => {
    onChange(
      produce(value, (draft: StatChartSpec) => {
        draft.show_series_name = option.value;

        // The default functionality for this property is "auto", so if the value is undefined or auto,
        // we can just delete this key from the object
        if (draft.show_series_name === undefined || draft.show_series_name === 'Auto') {
          delete draft.show_series_name;
        }
      })
    );
  };

  return (
    <OptionsEditorGrid>
      <OptionsEditorColumn>
        <OptionsEditorGroup title="Misc">
          <OptionsEditorControl
            label="Sparkline"
            control={<Switch checked={!!value.sparkline} onChange={handleSparklineChange} />}
          />
          <FontSizeSelector label="Value font size" value={value.value_font_size} onChange={handleFontSizeChange} />
          <FontSizeSelector
            label="Series name font size"
            value={value.series_name_font_size}
            onChange={handleSeriesNameFontSizeChange}
          />
          <FontSizeSelector
            label="Message font size"
            value={value.message_font_size}
            onChange={handleMessageFontSizeChange}
          />
          <OptionsEditorControl
            label="Series name"
            control={
              <SettingsAutocomplete
                value={SHOW_SERIES_NAME_OPTIONS.find((o) => o.value === value.show_series_name)}
                options={SHOW_SERIES_NAME_OPTIONS}
                getOptionLabel={(o) => o.label}
                onChange={handleShowSeriesNameChange}
                disableClearable
              />
            }
          />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <OptionsEditorGroup title={UNIT_SETTINGS_NAME}>
          <UnitSelector value={value.unit} onChange={handleUnitChange} />
        </OptionsEditorGroup>
      </OptionsEditorColumn>
      <OptionsEditorColumn>
        <StatChartThresholdsEditor hideThresholdMode thresholds={value.thresholds} onChange={handleThresholdsChange} />
      </OptionsEditorColumn>
    </OptionsEditorGrid>
  );
}
